import React from 'react';

import { useComments } from '../../../hooks/ui';
import { CommentForm } from './CommentForm';
import { CommentItem } from './CommentItem';

/**
 * Main component for displaying and managing task comments
 * Includes a form for adding comments and a scrollable list of existing comments
 *
 * @param {Object} props - The component props
 * @param {string} props.taskId - The ID of the task to display comments for
 * @param {string|null} props.caseId - The ID of the case associated with the task
 * @param {Object} props.userContext - The user context containing user details
 * @returns {JSX.Element} The rendered comments section
 */
const CommentsSection = ({ taskId, caseId, userContext }) => {
  const {
    comments,
    isAddingComment,
    isLoadingComments,
    isLoadingMoreComments,
    isUpdatingComment,
    deletingCommentId,
    editingComment,
    editCommentText,
    commentsListRef,
    setEditCommentText,
    addComment,
    startEditing,
    updateComment,
    cancelEditing,
    deleteComment,
    handleScroll,
  } = useComments(taskId, caseId, userContext);

  return (
    <div className="comments-section">
      <h5 className="modal-heading F-size16 text-bold">Comments ({comments.length})</h5>
      <hr />

      <div className="mb-3">
        <CommentForm onSubmit={addComment} isSubmitting={isAddingComment} />
      </div>

      <div
        className="comments-list thin-scrollbar"
        style={{
          maxHeight: '350px',
          overflowY: 'auto',
          paddingBottom: '10px',
          paddingRight: '5px',
        }}
        onScroll={handleScroll}
        ref={commentsListRef}
      >
        {isLoadingComments ? (
          <div className="d-flex justify-content-center p-3">
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
          </div>
        ) : comments.length > 0 ? (
          <>
            {comments.map((comment, index) => (
              <div
                key={comment.id}
                className="comment-item border-bottom pb-2"
                style={{
                  marginBottom: index === comments.length - 1 ? '20px' : '8px',
                }}
              >
                <CommentItem
                  comment={comment}
                  onEdit={startEditing}
                  onDelete={deleteComment}
                  isUpdatingComment={isUpdatingComment}
                  deletingCommentId={deletingCommentId}
                  editingComment={editingComment}
                  editCommentText={editCommentText}
                  setEditCommentText={setEditCommentText}
                  onSave={updateComment}
                  onCancel={cancelEditing}
                />
              </div>
            ))}
            {isLoadingMoreComments && (
              <div className="d-flex justify-content-center p-2">
                <i className="pi pi-spin pi-spinner" style={{ fontSize: '1.5rem' }}></i>
              </div>
            )}
          </>
        ) : (
          <div className="text-center p-3 text-muted">No comments yet</div>
        )}
      </div>
    </div>
  );
};

export default CommentsSection;
